import { hasCSSSelectorSupport } from "@otto-ec/otto-components-utils/utils/canIUse";
import { mount, unmount, type Component } from "svelte";
import { extendElementWithActionAttributes } from "../customElement/extendElementWithActionAttributes.ts";
import { useShadowStylesInjector } from "./utils/useShadowStylesInjector.ts";
import { setHost } from "./utils/useHost.ts";
import { createObservedAttributes } from "./utils/createObservedAttributes.ts";
import type { DefineComponentOptions } from "./types.ts";
import { setMounted } from "./utils/setMounted.ts";
import { resolveComponent } from "./utils/resolveComponent.ts";
import {
  parseAttributeToValue,
  stringifyValueToAttribute,
  type AttributeValueType,
} from "./utils/parseAttributeToValue.ts";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
/*                                                          */
export function defineComponent<C extends Component<any, any, any>>({
  tag,
  styles,
  props,
  html,
  extendOptions = {},
  component,
}: DefineComponentOptions<C>): void {
  const injectShadowStyles = useShadowStylesInjector(styles);

  const attributeMetaMap: Record<string, { propName: string; type?: AttributeValueType }> =
    Object.fromEntries(
      Object.entries(props).map(
        ([propName, { type, attribute = propName }]) => [attribute, { propName, type }] as const,
      ),
    );

  /**
 *
 *
 */
  /*                                                       */
  const reflecting = new Set<string>();

  let ElementClass = class extends HTMLElement implements Record<string, unknown> {
    static observedAttributes = createObservedAttributes(props);

    mounted: boolean = false;
    #ocEffectRootUnset?: () => void;
    $ocProps: Record<string, unknown> = {};
    $ocSvelteComponent?: Record<string, unknown>;
    [x: string]: unknown;

    attributeChangedCallback(name: string, _: string | null, newValue: string | null): void {
      if (reflecting.has(name)) return;
      const { propName, type } = attributeMetaMap[name];
      const value = parseAttributeToValue(newValue, type);

      if (this.$ocProps[propName] === value) return;

      this.$ocProps[propName] = value;
      if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;
    }

    async connectedCallback(): Promise<void> {
      if (html) (this.shadowRoot ? this.shadowRoot : this).innerHTML = html;

      /*                                                                              */
      const resolvedComponentOrPromise = component ? resolveComponent(component) : undefined;
      const resolvedComponent =
        Promise.resolve(resolvedComponentOrPromise) === resolvedComponentOrPromise
          ? await resolvedComponentOrPromise
          : (resolvedComponentOrPromise as C | undefined);

      Object.entries(props).forEach(([propName, { type }]) => {
        if (Object.hasOwn(this.$ocProps, propName)) return;
        if (Object.hasOwn(this, propName)) this.$ocProps[propName] = this[propName];
        else this.$ocProps[propName] = type === "Boolean" ? false : undefined;
      });

      if (resolvedComponent) {
        this.#ocEffectRootUnset = $effect.root(() => {
          const svelteState = $state(this.$ocProps);

          setHost(this);
          this.$ocSvelteComponent = mount(resolvedComponent, {
            target: this.shadowRoot ? this.shadowRoot : this,
            props: svelteState,
          });

          /*                                          */
          Object.assign(this, this.$ocSvelteComponent);
          setHost(undefined);

          /*                                                              */
          /*                                                              */
          Object.keys(props).forEach((propName) => {
            $effect(() => {
              (this as Record<string, unknown>)[propName] = svelteState[propName];
            });
          });
        });
      }

      await injectShadowStyles(this);

      setMounted(tag, this, true);
    }

    async disconnectedCallback(): Promise<void> {
      this.#ocEffectRootUnset?.();
      if (this.$ocSvelteComponent) {
        await unmount(this.$ocSvelteComponent);
        this.$ocSvelteComponent = undefined;
      }

      setMounted(tag, this, false);
    }
  };

  Object.entries(props).forEach(([propName, { type, attribute = propName, reflect }]) => {
    Object.defineProperty(ElementClass.prototype, propName, {
      get(this: InstanceType<typeof ElementClass>): unknown {
        return this.$ocProps[propName];
      },
      set(this: InstanceType<typeof ElementClass>, value: unknown) {
        if (this.$ocProps[propName] === value) return;
        this.$ocProps[propName] = value;
        if (this.$ocSvelteComponent) this.$ocSvelteComponent[propName] = value;

        /*                                                                   */
        /*                                                                   */
        /*                                                                  */
        /*                                                                   */
        /*                                                                    */
        if (reflect) {
          reflecting.add(propName);
          stringifyValueToAttribute(this, attribute, value, type);
          reflecting.delete(propName);
        }
      },
    });
  });

  ElementClass = window.__components.extend(extendOptions)(ElementClass) as never;

  /*                                             */
  /*                                                    */
  if (!hasCSSSelectorSupport)
    ElementClass = extendElementWithActionAttributes(ElementClass as never) as never;

  customElements.define(tag, ElementClass);
}

window.__components ??= {} as never;
window.__components.defineComponent = defineComponent;
